import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { ChevronsUpDown, Check } from "lucide-react";
import { cn } from "@/lib/utils";
import { clientAPI } from "@/lib/api";
import { toast } from "sonner";

const emptyClientForm = { type: "client", company: "", trade_name: "", first_name: "", last_name: "", email: "", phone: "", address: "", city: "", postal_code: "", country: "France", siret: "", notes: "" };

export function ClientCombobox({ clients, value, onChange }) {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const selectedClient = clients.find(c => c.id === value);
  const displayName = selectedClient ? (selectedClient.company || `${selectedClient.first_name} ${selectedClient.last_name}`) : "";

  const filtered = searchTerm
    ? clients.filter(c => {
        const name = (c.company || `${c.first_name} ${c.last_name}`).toLowerCase();
        return name.includes(searchTerm.toLowerCase());
      })
    : clients;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" role="combobox" aria-expanded={open} className="w-full justify-between font-normal h-9" data-testid="doc-client-select">
          {displayName || <span className="text-muted-foreground">Rechercher un client...</span>}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[400px] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput placeholder="Tapez pour rechercher..." value={searchTerm} onValueChange={setSearchTerm} data-testid="client-search-input" />
          <CommandList>
            <CommandEmpty>Aucun client trouvé</CommandEmpty>
            <CommandGroup>
              {filtered.slice(0, 50).map(c => {
                const name = c.company || `${c.first_name} ${c.last_name}`;
                return (
                  <CommandItem key={c.id} value={c.id} onSelect={() => { onChange(c.id); setOpen(false); setSearchTerm(""); }} data-testid={`client-option-${c.id}`}>
                    <Check className={cn("mr-2 h-4 w-4", value === c.id ? "opacity-100" : "opacity-0")} />
                    <span>{name}</span>
                    {c.type === "prospect" && <span className="ml-2 text-xs text-amber-600">(prospect)</span>}
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export function InlineClientDialog({ open, onOpenChange, onCreated }) {
  const [form, setForm] = useState({ ...emptyClientForm });
  const [saving, setSaving] = useState(false);
  const f = (k, v) => setForm({ ...form, [k]: v });

  const save = async () => {
    if (!form.company && !form.last_name) { toast.error("Nom ou société requis"); return; }
    setSaving(true);
    try {
      const res = await clientAPI.create(form);
      toast.success("Client créé");
      onCreated(res.data);
      onOpenChange(false);
      setForm({ ...emptyClientForm });
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="inline-client-dialog">
        <DialogHeader><DialogTitle className="text-sm">Nouveau client rapide</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Type</Label>
            <Select value={form.type} onValueChange={v => f("type", v)}>
              <SelectTrigger className="h-8 text-sm" data-testid="quick-client-type"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="client">Client</SelectItem><SelectItem value="prospect">Prospect</SelectItem></SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Société *</Label>
            <Input value={form.company} onChange={e => f("company", e.target.value)} className="h-8 text-sm" data-testid="quick-client-company" />
          </div>
          <div>
            <Label className="text-xs">Nom commercial</Label>
            <Input value={form.trade_name} onChange={e => f("trade_name", e.target.value)} className="h-8 text-sm" data-testid="quick-client-trade-name" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label className="text-xs">Prénom</Label><Input value={form.first_name} onChange={e => f("first_name", e.target.value)} className="h-8 text-sm" data-testid="quick-client-firstname" /></div>
            <div><Label className="text-xs">Nom</Label><Input value={form.last_name} onChange={e => f("last_name", e.target.value)} className="h-8 text-sm" data-testid="quick-client-lastname" /></div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label className="text-xs">Email</Label><Input type="email" value={form.email} onChange={e => f("email", e.target.value)} className="h-8 text-sm" data-testid="quick-client-email" /></div>
            <div><Label className="text-xs">Téléphone</Label><Input value={form.phone} onChange={e => f("phone", e.target.value)} className="h-8 text-sm" data-testid="quick-client-phone" /></div>
          </div>
          <Button onClick={save} disabled={saving} className="w-full bg-slate-900 hover:bg-slate-800 text-white text-xs h-8" data-testid="quick-client-save">
            {saving ? "..." : "Creer le client"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
